/** CloudFront distribution used for full-match / clip MP4s (see mobile matchClipStorage). */
export const STATBOX_CLOUDFRONT_HOST = 'd1nvampodjxeg6.cloudfront.net';

const S3_MATCH_CLIPS_HOST = 'statbox-videos-prod.s3.eu-north-1.amazonaws.com';

const CF_PROXY_PREFIX = '/__cf_proxy';
const S3_PROXY_PREFIX = '/__s3_proxy';

/**
 * Route CloudFront/S3 URLs through same-origin proxy paths so canvas
 * screenshots can draw the frame. CloudFront does not send CORS headers,
 * so a <video crossOrigin> load of the raw CF URL fails in production.
 *
 * Playback stays on CloudFront/S3 in production (Vercel FDT). Only the
 * one-frame screenshot copy should use this rewrite.
 *
 * Dev: vite.config.mjs server.proxy (/__cf_proxy, /__s3_proxy).
 * Prod: vercel.json rewrites those paths to CloudFront/S3.
 */
function rewriteCloudFrontOrS3ToProxy(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    if (u.hostname === STATBOX_CLOUDFRONT_HOST) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${CF_PROXY_PREFIX}${u.pathname}${u.search}`;
    }
    if (u.hostname === S3_MATCH_CLIPS_HOST) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      return `${origin}${S3_PROXY_PREFIX}${u.pathname}${u.search}`;
    }
  } catch {
    /* keep url */
  }
  return url;
}

function isDesktopApp() {
  try {
    if (typeof window === 'undefined') return false;
    return Boolean(
      window.statboxDesktop?.isDesktop
      || document.documentElement?.dataset?.statboxDesktop === '1',
    );
  } catch {
    return false;
  }
}

/** Dev / desktop app: rewrite playback URLs through the same-origin media proxy. */
export function rewriteMediaUrlForSameOriginProxy(url) {
  if (!import.meta.env.DEV && !isDesktopApp()) return url;
  return rewriteCloudFrontOrS3ToProxy(url);
}

/** Desktop: VideoLoft MPEG-TS must be same-origin (auth.statbox.co CORS is www.statbox.co only). */
export function rewriteVideoloftStreamUrlForDesktop(url) {
  if (!url || !isDesktopApp()) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.hostname === 'auth.statbox.co' && parsed.pathname.includes('videoloft-proxy-hls')) {
      return `${window.location.origin}/__vl_proxy${parsed.search}`;
    }
  } catch {
    /* keep url */
  }
  return url;
}

/**
 * Screenshot capture copy — same-origin in both dev and production so
 * canvas is not tainted. Visible players must not use this for playback.
 * A full-match MP4 on this path is billed as Vercel Fast Data Transfer.
 */
export function rewriteMediaUrlForCaptureProxy(url) {
  return rewriteCloudFrontOrS3ToProxy(url);
}

/**
 * CloudFront and S3 do not send CORS for the site origin. Setting
 * crossOrigin="anonymous" on those URLs makes the browser refuse playback.
 * Omit it and let screenshots use the one-frame capture proxy instead.
 */
export function crossOriginForPlayback(url) {
  if (!url || typeof url !== 'string') return undefined;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://statbox.local');
    if (u.hostname === STATBOX_CLOUDFRONT_HOST || u.hostname === S3_MATCH_CLIPS_HOST) return undefined;
  } catch {
    return 'anonymous';
  }
  return 'anonymous';
}

/**
 * Inverse of rewriteMediaUrlForCaptureProxy. Clip workers need the real
 * CloudFront/S3 object URL (or its pathname key), not /__cf_proxy|/__s3_proxy.
 */
export function unwrapMediaProxyUrl(url) {
  if (!url || typeof url !== 'string') return url;
  try {
    const u = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'https://statbox.local');
    if (u.pathname.startsWith(`${CF_PROXY_PREFIX}/`) || u.pathname === CF_PROXY_PREFIX) {
      return `https://${STATBOX_CLOUDFRONT_HOST}${u.pathname.slice(CF_PROXY_PREFIX.length)}${u.search}`;
    }
    if (u.pathname.startsWith(`${S3_PROXY_PREFIX}/`) || u.pathname === S3_PROXY_PREFIX) {
      return `https://${S3_MATCH_CLIPS_HOST}${u.pathname.slice(S3_PROXY_PREFIX.length)}${u.search}`;
    }
  } catch {
    /* keep url */
  }
  return url;
}

/** @deprecated alias — use rewriteMediaUrlForSameOriginProxy */
export const rewriteMediaUrlForLocalDev = rewriteMediaUrlForSameOriginProxy;
